/**
 * The signed-in user, as the backend describes them.
 *
 * `role` is the ONLY thing the UI may branch on. The API token carries no role —
 * its claims are the username, an issued-at, an expiry and a session generation,
 * nothing else — and the server re-reads the role from the database on every
 * request. So decoding the JWT to decide what to show would be reading a claim that
 * does not exist.
 */

/**
 * `DISPLAY` is a wall screen, not a person: the flight map and nothing else, with a
 * thirty-day session that renews itself. See `isDisplay`.
 */
export const USER_ROLES = ['ADMIN', 'AAK', 'USER', 'KGA_USER', 'INTEGRATOR', 'OPERATOR', 'DISPLAY'] as const;

export type UserRole = (typeof USER_ROLES)[number];

/**
 * The longest name the server stores (`users.name` is 200 characters). The inputs
 * stop there, so a name cannot be typed that the server would refuse for its length.
 */
export const NAME_MAX_LENGTH = 200;

/**
 * An account: a login, a name and a role — nothing else. There is no company, no
 * name in parts and no contact card; the server stored those and never used them,
 * and they are gone from every request and response.
 */
export interface UserInfo {
  id: number;
  /**
   * The login, and the identity the token is issued against. Set at creation and
   * never editable — no edit endpoint has a field for it.
   */
  username: string;
  /**
   * Who the account is: a person's full name (`Бисембин Алдияр`), or what the account
   * is when it is not a person (`Диспетчерская ALA`). One field for both, required
   * on every account.
   *
   * Typed nullable although the server always sends it: a backend older than the
   * field has none, and then the login is shown in its place rather than a blank.
   */
  name: string | null;
  /** A user has exactly one role, or none at all — in which case everything 403s. */
  role: UserRole | null;
  /**
   * Whether the account may sign in. Read it through `isUserActive`, never
   * directly: both spellings are declared and only one of them arrives.
   */
  isActive?: boolean;
  active?: boolean;
  created: string | null;
  updated: string | null;
}

/**
 * Whether this account may sign in — `false` is what "deleted" means here.
 *
 * Both names are read because the two descriptions of this response disagree: the
 * API documentation shows `isActive`, and the type this app was originally built
 * from the login response carries `active`. They are the same Jackson-serialized
 * field and which name arrives depends on the getter that produced it.
 *
 * Picking one and being wrong would invert the state on every row of the one screen
 * whose job is to change it — every working account shown as blocked, and the
 * button beside it offering to do the opposite of what it says. Reading whichever
 * arrived cannot be wrong either way.
 *
 * Neither present means the response is not what either description says, and
 * active is the normal case: showing a working account as blocked would send an
 * administrator to re-enable something that was never off.
 */
export const isUserActive = (user: UserInfo): boolean => user.isActive ?? user.active ?? true;

/**
 * The body of `POST /user/add`.
 *
 * **Every field is required**, and the server refuses the create without any of them:
 * a login of 4–30 characters (stored in lower case), a password of at least six, a
 * name that is not only spaces, and a role.
 */
export interface UserRegisterCard {
  username: string;
  password: string;
  name: string;
  role: UserRole;
}

/**
 * The body of `POST /user/edit/id/{userId}` — an administrator editing anyone.
 *
 * **Only the fields present are applied**, so `{ name: 'Диспетчерская ALA' }` is a
 * complete request and everything else is left alone. There is no `username` here:
 * it is the identity the token is issued against and cannot be changed.
 *
 * `name` may be left out but not blanked — a name of only spaces is refused.
 *
 * `role` is optional and takes effect. It used to be required AND ignored — a
 * client that sends it on every edit is describing the old behaviour, so send it
 * only when the role is actually being changed.
 */
export interface UserEditCard {
  name?: string;
  role?: UserRole;
  /** Set directly, with no current password — an administrator resetting one. */
  password?: string;
}

/**
 * The body of `POST /user/me` — a user editing their own account.
 *
 * A DIFFERENT shape from `UserEditCard`, and the difference is the point: there is
 * no `role`, no `isActive`, no `username`. Raising your own privileges through this
 * endpoint is not refused, it is unrepresentable.
 *
 * `newPassword` requires `currentPassword`; a wrong or missing one is refused with a
 * `DataError` on `currentPassword` and nothing is changed, so a session someone else
 * has taken over cannot be used to lock the owner out.
 *
 * `name` may be left out but not blanked, as on `UserEditCard`.
 */
export interface UserProfileCard {
  name?: string;
  currentPassword?: string;
  newPassword?: string;
}

/** A successful login. */
export interface LoginSuccess {
  token: string;
  /**
   * A second, unrelated token for Kibana. NEVER send this to this API: it is
   * signed with the same key, so if `elk.login` happens to exist as an active
   * user, the caller would be authenticated as THAT account with its role.
   */
  elkToken: string | null;
  userInfo: UserInfo;
}

/**
 * A failed login — which also arrives as HTTP 200.
 *
 * `LoginController` builds it with `ResponseEntity.of(Optional.of(...))`, and that
 * is 200 whenever a body is present. Every reason collapses into one shape: wrong
 * password, unknown username, blocked account and server error are
 * indistinguishable. Deliberate for the first three; check the server logs for the
 * fourth.
 */
export interface LoginFailure {
  path: string | null;
  errorCode: string;
  message: string;
}

/**
 * Which roles may reach a screen, taken from SecurityConfig rather than guessed.
 *
 * OPERATOR is absent from every list on purpose: the enum describes it as the role
 * that creates and edits schedules, but SecurityConfig never names it, so it 403s
 * on the very screens it is named for. Listing it here would promise access the
 * server refuses.
 */
/**
 * The board — the landing screen, and the only one a non-administrator has.
 *
 * INTEGRATOR and OPERATOR are absent, so `/schedule` is routed on this rather than
 * on "any signed-in role": without the guard those two would land on a board whose
 * every request answers 403.
 */
export const ROLES_ONLINE_TABLE: readonly UserRole[] = ['ADMIN', 'AAK', 'USER', 'KGA_USER'];

/**
 * The flight map: the board's roles, and a wall screen. `SecurityConfig` opens
 * `GET /online-table/airborne` — and the airport-name lookups the map makes — to
 * DISPLAY, and nothing else on the online table.
 */
export const ROLES_MAP: readonly UserRole[] = [...ROLES_ONLINE_TABLE, 'DISPLAY'];

/**
 * What the SERVER allows on `/airport/**` and `/airline/**`.
 *
 * **The app is deliberately stricter**: both dictionary screens are routed
 * ADMIN-only, because every other role works the board and nothing else, and a
 * dictionary they cannot change is a screen with no use for them. This list is kept
 * because it records the server's actual rule — the thing that would have to be
 * re-derived if that product decision is revisited — not because anything routes on
 * it today.
 *
 * Narrowing the UI below what the server permits is always safe. The reverse never
 * is, which is why these lists are copied from SecurityConfig rather than guessed.
 */
export const ROLES_DICTIONARY_READ: readonly UserRole[] = ['ADMIN', 'AAK', 'USER', 'KGA_USER'];
export const ROLES_DICTIONARY_WRITE: readonly UserRole[] = ['ADMIN'];
export const ROLES_ADMIN_ONLY: readonly UserRole[] = ['ADMIN'];

/**
 * A record, not a guard: there is no international-arrivals screen yet.
 *
 * Kept for the same reason as `ROLES_DICTIONARY_READ` — it is what `SecurityConfig`
 * says, and re-deriving it later from the server is the part that goes wrong.
 */
export const ROLES_INTERNATIONAL_ARRIVALS: readonly UserRole[] = ['ADMIN', 'AAK'];

export const hasRole = (user: UserInfo | null, allowed: readonly UserRole[]): boolean =>
  user?.role != null && allowed.includes(user.role);

/**
 * A wall screen. It gets the map full-screen with no navigation, and a session that
 * renews itself once a day — the server refuses it everything but the map, so there
 * is nowhere else to go and nobody standing at it to sign in again.
 */
export const isDisplay = (user: UserInfo | null): boolean => user?.role === 'DISPLAY';

/**
 * Who is signed in, for the header: the account's name, or the username when there
 * is none — see `UserInfo.name`.
 */
export const displayName = (user: UserInfo | null): string => {
  if (!user) return '';

  return nameOf(user) || user.username;
};

/**
 * The account's name, or empty when it has none.
 *
 * Empty rather than the username: in the list this sits beside the username, and
 * repeating it there would read as a name.
 */
export const nameOf = (user: UserInfo): string => user.name?.trim() ?? '';
